import { columnAdderOffset, maxLeftBounds, maxTopBounds } from '../constants/gridConfig';

import {
  BORDER_GRAY,
  BG_BADGE,
  VALID_GREEN,
} from '../constants/constants';
import { getAccentColor } from './theme';
import { marginOnPageButton } from '../constants/gridConfig';
import _ from 'lodash';
import { boxHeight } from './boxes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

export const actionButtonBottomMargin = 8;

// Écart entre le bas de la barre et la place réservée au « + » : plus serré que
// la marge horizontale, sinon la barre paraît détachée du container.
export const floatyVerticalGap = 3;

// Position d'une barre flottante (box ou container) centrée au-dessus de son
// élément ancré, bornée horizontalement pour ne jamais empiéter sur les
// panels gauche/droite (rects réels : un panel replié est décalé hors écran,
// donc sans effet de bord ici).
//
// Verticalement, la barre réserve TOUJOURS la place du « + » d'ajout de colonne
// (columnAdderOffset), affiché ou non. Elle cherchait auparavant les « + » réels
// dans le DOM (`[data-icon="plus"]` à moins de 150px au-dessus, sans filtre
// horizontal) : sa hauteur dépendait donc de ce qui se trouvait à l'écran à cet
// instant — nombre de colonnes du container, survol de la bande d'ajout de
// ligne, et jusqu'aux boutons « + » du panel de droite, dont la position
// verticale change avec le type de box sélectionné. La barre sautait d'un cran
// selon la box, voire d'une sélection à l'autre sur la même box.
//
// Borne basse à `margin` (pas navbarHeight) : sur le tout premier container de
// la page, le calcul plaçait la barre par-dessus la top bar puis la
// reclampait juste en dessous — donc sur le container lui-même, faute de
// place. Elle remonte maintenant carrément sur la top bar (zIndex 9998 contre
// 1100 : elle passe par-dessus) plutôt que de recouvrir le container.
export const computeClampedFloatyPosition = ({ anchorTop, anchorLeft, anchorWidth, toolbarWidth, toolbarHeight, margin = 8 }) => {
  const leftBound = (document.getElementById('overlay-sider-left')?.getBoundingClientRect().right ?? 0) + margin;
  const rightBound = (document.getElementById('overlay-sider-right')?.getBoundingClientRect().left ?? window.innerWidth) - margin;
  let left = anchorLeft + anchorWidth / 2 - toolbarWidth / 2;
  left = Math.max(left, leftBound);
  left = Math.min(left, rightBound - toolbarWidth);
  const top = Math.max(
    margin,
    anchorTop - columnAdderOffset - floatyVerticalGap - toolbarHeight
  );
  return { left, top };
};

// Décalage horizontal à appliquer à une liste flottante centrée sur son bouton
// pour qu'elle tienne dans ce qui la rogne (le panel de droite, sinon la
// fenêtre). Débord à droite d'abord, puis à gauche : une liste plus large que
// son conteneur reste ainsi accrochée au bord gauche, là où elle se lit.
export const clampListHorizontalShift = ({ listLeft, listRight, boundsLeft, boundsRight, margin = 4 }) => {
  let shift = 0;
  const overflowRight = listRight - (boundsRight - margin);
  if (overflowRight > 0) shift -= overflowRight;
  const overflowLeft = boundsLeft + margin - (listLeft + shift);
  if (overflowLeft > 0) shift += overflowLeft;
  return shift;
};

// Géométrie de la bande « ajouter une ligne » posée sous une ligne d'un
// container. Elle occupe la gouttière laissée par « Marge entre les lignes »,
// mais plafonnée : passé une quinzaine de pixels ce n'est plus un interstice,
// c'est un pavé qui éloigne le « + » de sa ligne.
// Sous la DERNIÈRE ligne il n'y a pas de gouttière : la bande tomberait dans
// l'écart entre deux containers, où l'ajout de container occupe déjà toute la
// hauteur (AdderColumns). Elle chevauche donc le bord bas du container — son
// « + » reste au contact de sa ligne, et la fin de l'écart reste à l'ajout de
// container.
export const rowAdderBandGeometry = ({ lineSpacing, isLastRow }) => {
  const height = Math.min(Math.max(lineSpacing ?? 0, 8), 14);
  return { height, bottom: isLastRow ? -height / 2 : -height };
};

export const getBackgroundBorderStyle = (
  backgroundColor,
  border,
  style = {}
) => {
  return {
    ...style,
    width: '100%',
    backgroundClip: 'content-box',
    borderRadius: border?.radius + 'px',
    backgroundColor:
      backgroundColor?.label !== 'transparent' ? backgroundColor?.style : null,
    borderTop:
      border?.top === true
        ? border?.size + 'px solid ' + border?.color?.style
        : null,
    borderBottom:
      border?.bottom === true
        ? border?.size + 'px solid ' + border?.color?.style
        : null,
    borderLeft:
      border?.left === true
        ? border?.size + 'px solid ' + border?.color?.style
        : null,
    borderRight:
      border?.right === true
        ? border?.size + 'px solid ' + border?.color?.style
        : null,
  };
};

export const navbarElementStyle = {
  padding: 0,
  marginRight: marginOnPageButton,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
};

export const navbarButtonStyle = {
  ...navbarElementStyle,
  width: 32,
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: BORDER_GRAY,
  borderRadius: '5px',
  backgroundColor: 'white',
};

// Boutons en pilule : la référence est le bouton « Documents » du panel de
// gauche (.style_btn_right_side, calé sur Costing react-ui), repris tel quel par
// la liste des propositions. Les boutons antd par défaut gardaient leurs coins
// carrés (borderRadius 6 d'antd), ce qui donnait deux familles de boutons dans
// le même écran — pilule pour les uns, rectangle arrondi pour les autres.
// Posé sur le thème plutôt que bouton par bouton pour que ça reste vrai partout,
// y compris dans les modales et les popconfirms.
export const buttonPillRadius = 30;
export const pillButtonComponentsTheme = {
  Button: {
    borderRadius: buttonPillRadius,
    borderRadiusLG: buttonPillRadius,
    borderRadiusSM: buttonPillRadius,
  },
};

export const checkOffset = [-10, 8];
export const checkStyle = {
  fontSize: 14,
  color: VALID_GREEN,
  backgroundColor: BG_BADGE,
  borderRadius: 7,
};

export const iconButtonStyle = (active = false) => ({
  fontSize: 20,
  margin: 0,
  color: active ? getAccentColor() : 'black',
});

export const getPositionStyles = (box, isDragging) => {
  let { top, left } = box;
  if (left < maxLeftBounds) left = maxLeftBounds;
  if (top < maxTopBounds) top = maxTopBounds;
  let transform = `translate3d(${left}px, ${top}px, 0)`;
  if (isDragging) {
    const offsetMove = getRotationMove(box);
    transform = `translate3d(${left - offsetMove.x}px, ${
      top - offsetMove.y
    }px, 0)`;
  }
  return {
    position: 'absolute',
    transform,
    WebkitTransform: transform,
    // IE fallback: hide the real node using CSS when dragging
    // because IE will ignore our custom "empty image" drag preview.
  };
};

export const getRotationMove = (box) => {
  const { rotation, width } = box;
  if (rotation === 0) {
    return { x: 0, y: 0 };
  }
  const w = width;
  const h = boxHeight(box);
  const aplhaZeroRad = Math.atan(h / w);
  const aplhaOneRad = aplhaZeroRad + (rotation * Math.PI) / 180;
  const hyp = Math.sqrt((w * w) / 4 + (h * h) / 4);
  const xOne = hyp * Math.cos(aplhaOneRad);
  const yOne = hyp * Math.sin(aplhaOneRad);
  return {
    x: xOne - w / 2,
    y: yOne - h / 2,
  };
};

export const getRotationStyle = (rotation) => {
  // let offset = '';
  // if (isDragging) {
  //   const offsetMove = getRotationMove(box);
  //   offset = isDragging ? `translate3d(${offsetMove.x}px, ${offsetMove.y}px, 0) ` : '';
  // }
  const transform = `rotate(${rotation}deg)`;
  return {
    transform,
    WebkitTransform: transform,
    // IE fallback: hide the real node using CSS when dragging
    // because IE will ignore our custom "empty image" drag preview.
  };
};

export const getDragStyles = (isDragging, isHovered) => {
  const shouldNotDisplay = isDragging && isHovered;
  return {
    opacity: shouldNotDisplay ? 0 : 1,
    height: shouldNotDisplay ? 0 : '',
  };
};

export const boxActionColor = ({ selected, inGroup, hovered }) => {
  let color;
  switch (true) {
    case selected:
      color = 'transparent';
      break;
    case hovered:
      color = '#BAD6F2';
      break;
    case inGroup:
      color = '#5499DE';
      break;
    default:
      color = 'transparent';
      break;
  }
  // if (!selected && !inGroup && hovered) {
  //   color = "#5499DE";
  // } else if (selected && hovered) {
  //   color = '#BAD6F2';
  // } else if (inGroup && hovered) {
  //   color = '#BAD6F2';
  // } else if (selected) {
  //   color = "#5499DE";
  // } else if (inGroup) {
  //   color = "#5499DE";
  // } else {
  //   color = 'transparent';
  // }
  return color;
};

/**
 * Cadre de sélection d'une box. Les membres d'une multi-sélection (ctrl/cmd +
 * clic en mode dessin) ne portaient qu'un liseré bleu clair collé au contenu :
 * invisible sur une forme sombre, on ne voyait pas ce qui était sélectionné.
 * Ils prennent maintenant un pointillé à la couleur du thème, décalé du contenu
 * (donc tracé sur le fond de page), pour rester lisible.
 * La box maîtresse porte le même pointillé tant que la multi-sélection dure :
 * avec son seul cadre plein collé au contenu elle avait l'air exclue de la
 * sélection. Elle reste reconnaissable à ses poignées de redimensionnement.
 */
export const selectionOutline = ({
  remoteLock,
  isSelected,
  isInSelectedGroup,
  isMultiSelection,
  isHovered,
  accentColor,
}) => {
  if (remoteLock) return { outline: `2px solid ${remoteLock.color}` };
  if (isInSelectedGroup || (isSelected && isMultiSelection))
    return { outline: `2px dashed ${accentColor}`, outlineOffset: 3 };
  return {
    outline: `2px solid ${boxActionColor({
      selected: isSelected,
      inGroup: isInSelectedGroup,
      hovered: isHovered,
    })}`,
  };
};

export const resizeHandlersPresence = ({
  top = false,
  right = false,
  bottom = false,
  left = false,
  topRight = false,
  bottomRight = false,
  bottomLeft = false,
  topLeft = false,
}) => ({
  top,
  right,
  bottom,
  left,
  topRight,
  bottomRight,
  bottomLeft,
  topLeft,
});

const RESIZE_CURSORS = ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'];

// Curseur d'une poignée dont l'axe tourne avec la box. `handleAngle` est l'angle
// de cet axe sans rotation (0 pour « right », 90 pour « bottom ») ; l'axe n'a
// pas de sens, donc on raisonne modulo 180°, au quart de tour et demi le plus
// proche.
const resizeCursor = (handleAngle, rotation) => {
  const angle = (((handleAngle + (rotation || 0)) % 180) + 180) % 180;
  return RESIZE_CURSORS[Math.round(angle / 45) % RESIZE_CURSORS.length];
};

export const resizeHandlersStyles = (rotation = 0) => ({
  bottom: {
    width: '30px',
    height: '7px',
    marginTop: -3,
    marginLeft: -15,
    border: '1px solid ' + getAccentColor(),
    backgroundColor: 'white',
    borderRadius: '25px',
    left: '50%',
    cursor: resizeCursor(90, rotation),
    boxSizing: 'border-box',
    zIndex: 1000,
    pointerEvents: 'auto',
    // transform: `rotate(${rotation}deg)`,
  },
  right: {
    width: '7px',
    height: '30px',
    marginTop: -15,
    marginLeft: -3,
    border: '1px solid ' + getAccentColor(),
    backgroundColor: 'white',
    borderRadius: '25px',
    top: '50%',
    cursor: resizeCursor(0, rotation),
    boxSizing: 'border-box',
    zIndex: 1000,
    pointerEvents: 'auto',
    // transform: `rotate(${rotation}deg)`,
  },
});

// Colonnes de l'éditeur de formulaire. Les colonnes de contenu (middle) sont
// des cartes bordées (même traitement que la carte "Valeur par défaut",
// cf. DefaultValueBox) : sur fond blanc, les anciens filets séparateurs ne
// suffisaient plus à structurer la page. L'espacement entre colonnes vient du
// gap des grilles parentes (FormCreator / FormBoxEditor), plus des filets.
export const variableStyle = {
  left: {
    paddingRight: 8,
  },
  middle: {
    border: '1px solid rgba(11, 34, 57, 0.15)',
    borderRadius: 8,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  right: {
    paddingLeft: 8,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
};

/**
 * Merges a type of styles (tableStyles, selectorStyles, etc.)
 * while reassigning IDs to avoid collisions.
 * @param {Object} configuration - The current draft configuration
 * @param {Object} sectionConfiguration - The section configuration
 * @param {string} sectionId - The unique identifier of the section
 * @param {string} propertyKey - The key of the styles array (e.g., 'tableStyles')
 * @param {string} sectionTitle - The title of the section
 * @param {string} dateTimestamp - The timestamp of the injection
 * @returns {Array} - The merged array of styles
 */
function mergeStylesForProperty({
  configuration,
  sectionConfiguration,
  sectionId,
  sectionTitle,
  dateTimestamp,
  propertyKey,
}) {
  // Existing array in the current configuration
  const existingStyles = configuration[propertyKey] || [];
  let sectionStyles = sectionConfiguration[`${propertyKey}`] || [];
  // Styles sans typographie propre : rien à reprendre de la police du
  // document, y injecter fontSize/color/fontFamily ne ferait qu'ajouter des
  // clés mortes.
  if (!['tableStyles', 'accordionPanelStyles', 'infoCardStyles'].includes(propertyKey)) {
    sectionStyles = mutateImportImmutableStyleValue(
      sectionStyles,
      configuration
    );
  }

  // Reassign IDs for styles coming from the section
  const updatedStylesFromSection = sectionStyles.map((originalStyle) => {
    let newId = originalStyle.id;
    newId = `${newId}_section_${sectionId}_time_${dateTimestamp}`;
    return {
      ...originalStyle,
      name: `${originalStyle.name} - (Provient de la section : ${sectionTitle})`,
      id: newId,
    };
  });

  // Une occurrence de section récursive s'injecte en plusieurs segments (autour
  // de ses sections enfants), tous avec le même sectionId et le même
  // horodatage : sans ce filtre, ses styles étaient ajoutés une fois par segment.
  const existingIds = new Set(existingStyles.map(({ id }) => id));
  // Chaque section arrive avec son propre style « Défaut », jamais modifié :
  // l'ajouter faisait grossir les listes d'une entrée par section liée. Un
  // style identique (hors id et nom) à un style déjà présent n'est pas importé,
  // les box de la section sont rebranchées dessus (cf. resolveSectionStyleId).
  return [
    ...existingStyles,
    ...updatedStylesFromSection.filter(
      (style) =>
        !existingIds.has(style.id) &&
        !(
          DEDUPLICATED_STYLE_KEYS.includes(propertyKey) &&
          existingStyles.some((existing) => stylesEquivalent(existing, style))
        )
    ),
  ];
}

// Listes de styles dont un doublon exact n'est pas importé. Les styles de
// texte restent importés tels quels : leur identifiant est aussi repris dans
// le contenu des éditeurs.
const DEDUPLICATED_STYLE_KEYS = [
  'tableStyles',
  'checkboxStyles',
  'selectorStyles',
  'accordionPanelStyles',
  'infoCardStyles',
];

// `fromTmp` marque les styles de section liée (mutateMergeTmpStyles) : il ne
// change pas le rendu.
const stylesEquivalent = (a, b) =>
  _.isEqual(
    _.omit(a, ['id', 'name', 'fromTmp']),
    _.omit(b, ['id', 'name', 'fromTmp'])
  );

const withoutTwinStyles = (incoming, existing) =>
  incoming.filter((style) => !existing.some((other) => stylesEquivalent(other, style)));

// Champ par lequel une box désigne son style. Cartes info et volets copient
// leur style dans la box à la création : aucune référence à rebrancher.
const BOX_STYLE_ID_FIELDS = {
  tableStyles: 'tableStyleId',
  checkboxStyles: 'checkboxStyleId',
  selectorStyles: 'selectorStyleId',
};

/**
 * Retire les doublons laissés par les injections de section d'avant le
 * dédoublonnage de mergeStylesForProperty : un style importé d'une section
 * (id suffixé) identique, hors id et nom, à un style qui le précède dans la
 * liste. Les box qui le désignaient sont rebranchées sur ce style. Les styles
 * créés dans le document ne sont jamais retirés, ni le premier de chaque
 * liste (modèle des cartes info et volets déposés).
 * Renvoie le contenu tel quel quand il n'y a rien à retirer.
 */
export const dedupeSectionStyles = (content) => {
  const configuration = content?.configuration;
  if (!configuration) return content;

  const nextConfiguration = { ...configuration };
  const replacedIds = {};
  DEDUPLICATED_STYLE_KEYS.forEach((propertyKey) => {
    const styles = configuration[propertyKey];
    if (!Array.isArray(styles) || styles.length < 2) return;
    const kept = [];
    const replaced = new Map();
    styles.forEach((style, index) => {
      const twin =
        index > 0 && isStyleFromSection(style)
          ? kept.find((existing) => stylesEquivalent(existing, style))
          : null;
      if (twin) replaced.set(style.id, twin.id);
      else kept.push(style);
    });
    if (replaced.size === 0) return;
    nextConfiguration[propertyKey] = kept;
    replacedIds[propertyKey] = replaced;
  });
  if (Object.keys(replacedIds).length === 0) return content;

  const rebindBox = (box) =>
    Object.entries(BOX_STYLE_ID_FIELDS).reduce((next, [propertyKey, field]) => {
      const keptId = replacedIds[propertyKey]?.get(box[field]);
      return keptId === undefined ? next : { ...next, [field]: keptId };
    }, box);

  return {
    ...content,
    configuration: nextConfiguration,
    boxes: content.boxes?.map(rebindBox),
    masks: content.masks?.map((mask) =>
      mask.boxes ? { ...mask, boxes: mask.boxes.map(rebindBox) } : mask
    ),
  };
};

/**
 * Identifiant, dans la configuration du document, du style d'une box de section
 * injectée : le style suffixé s'il a été importé, sinon le style équivalent
 * existant (cf. mergeStylesForProperty). `importedStyles` : styles de section
 * liée importés à part (draft.tmp, cf. mutateMergeTmpStyles).
 */
export const resolveSectionStyleId = ({
  configuration,
  sectionConfiguration,
  propertyKey,
  styleId,
  sectionId,
  dateTimestamp,
  importedStyles = [],
}) => {
  const suffixedId = `${styleId}_section_${sectionId}_time_${dateTimestamp}`;
  const styles = configuration[propertyKey] || [];
  if (
    !DEDUPLICATED_STYLE_KEYS.includes(propertyKey) ||
    styles.some(({ id }) => id === suffixedId) ||
    importedStyles.some(({ id }) => id === suffixedId)
  ) {
    return suffixedId;
  }
  const source = (sectionConfiguration[propertyKey] || []).find(
    ({ id }) => id === styleId
  );
  if (!source) return suffixedId;
  const [imported] = [
    'tableStyles',
    'accordionPanelStyles',
    'infoCardStyles',
  ].includes(propertyKey)
    ? [source]
    : mutateImportImmutableStyleValue([source], configuration);
  return (
    styles.find((existing) => stylesEquivalent(existing, imported))?.id ??
    suffixedId
  );
};

export const mutateMergeStyles = ({
  draft,
  sectionConfiguration,
  sectionId,
  sectionTitle,
  dateTimestamp,
}) => {
  // Sans dédoublonnage, chaque section injectée (et chaque sous-section)
  // réajoutait sa palette entière : les couleurs se multipliaient.
  draft.configuration.colors = uniqueColors([
    ...draft.configuration.colors,
    ...sectionConfiguration.colors,
  ]);
  draft.configuration.highlightColors = uniqueColors([
    ...draft.configuration.highlightColors,
    ...sectionConfiguration.highlightColors,
  ]);
  draft.configuration.tableStyles = mergeStylesForProperty({
    configuration: draft.configuration,
    sectionConfiguration,
    sectionId,
    sectionTitle,
    dateTimestamp,
    propertyKey: 'tableStyles',
  });
  draft.configuration.checkboxStyles = mergeStylesForProperty({
    configuration: draft.configuration,
    sectionConfiguration,
    sectionId,
    sectionTitle,
    dateTimestamp,
    propertyKey: 'checkboxStyles',
  });
  draft.configuration.accordionPanelStyles = mergeStylesForProperty({
    configuration: draft.configuration,
    sectionConfiguration,
    sectionId,
    sectionTitle,
    dateTimestamp,
    propertyKey: 'accordionPanelStyles',
  });
  draft.configuration.infoCardStyles = mergeStylesForProperty({
    configuration: draft.configuration,
    sectionConfiguration,
    sectionId,
    sectionTitle,
    dateTimestamp,
    propertyKey: 'infoCardStyles',
  });
  draft.configuration.selectorStyles = mergeStylesForProperty({
    configuration: draft.configuration,
    sectionConfiguration,
    sectionId,
    sectionTitle,
    dateTimestamp,
    propertyKey: 'selectorStyles',
  });
  draft.configuration.textStyles = mergeStylesForProperty({
    configuration: draft.configuration,
    sectionConfiguration,
    sectionId,
    sectionTitle,
    dateTimestamp,
    propertyKey: 'textStyles',
  });
  const mergedFontFamilies = [
    ...sectionConfiguration.fontFamilyList,
    ...draft.configuration.fontFamilyList,
  ].reduce((unique, item) => {
    const exists = unique.find((x) => x.key === item.key);
    if (!exists) {
      unique.push(item);
    }
    return unique;
  }, []);

  draft.configuration.fontFamilyList = mergedFontFamilies;
};

export const mutateImportImmutableStyleValue = (styles, configuration) => {
  return styles.map((style) => {
    if (style.id !== 0) return style;
    return {
      ...style,
      fontSize: configuration.fontSize,
      color: configuration.baseFontColor,
      fontFamily: configuration.fontFamily,
    };
  });
};

export const mutateMergeTmpStyles = ({
  draft,
  sectionConfiguration,
  sectionId,
  sectionTitle,
  dateTimestamp,
}) => {
  const excludedStyles = new Set([
    'transparent',
    'rgba(0, 0, 0, 0)',
    'rgba(255, 255, 255, 1.0)',
  ]);

  const excludedLabels = new Set(['transparent', 'white', 'black']);

  const fromTmpColors = (draft.tmp?.colors || []).map((item) => ({
    ...item,
    fromTmp: true,
  }));

  const fromSectionColors = sectionConfiguration?.colors?.filter(
    (item) => !excludedStyles.has(item.style) && !excludedLabels.has(item.label)
  );

  const mergedColorArray = [
    ...fromTmpColors,
    ...fromSectionColors.map((item) => ({
      ...item,
      fromTmp: true,
    })),
  ];

  const fromTmpHighlightColors = (draft.tmp?.highlightColors || []).map(
    (item) => ({
      ...item,
      fromTmp: true,
    })
  );

  const fromSectionHighlightColors =
    sectionConfiguration.highlightColors.filter(
      (item) =>
        !excludedStyles.has(item.style) && !excludedLabels.has(item.label)
    );

  const mergedHighlightColorArray = [
    ...fromTmpHighlightColors,
    ...fromSectionHighlightColors.map((item) => ({
      ...item,
      fromTmp: true,
    })),
  ];

  const mergedTableStyleArray = [
    ...(draft.tmp?.tableStyles ? draft.tmp.tableStyles : []),
    ...withoutTwinStyles(sectionConfiguration.tableStyles.map((item) => ({
      ...item,
      id: `${item.id}_section_${sectionId}_time_${dateTimestamp}`,
      name: (
        <span>
          {item.name} - ( Provient de la section liée{' '}
          <FontAwesomeIcon icon={icon({ name: 'lock' })} /> : {sectionTitle})
        </span>
      ),
      fromTmp: true,
    })), draft.configuration.tableStyles || []),
  ];

  const mergedCheckboxStyleArray = [
    ...(draft.tmp?.checkboxStyles ? draft.tmp.checkboxStyles : []),
    ...withoutTwinStyles(mutateImportImmutableStyleValue(
      sectionConfiguration.checkboxStyles,
      draft.configuration
    ).map((item) => ({
      ...item,
      id: `${item.id}_section_${sectionId}_time_${dateTimestamp}`,
      name: (
        <span>
          {item.name} - ( Provient de la section liée{' '}
          <FontAwesomeIcon icon={icon({ name: 'lock' })} /> : {sectionTitle})
        </span>
      ),
      fromTmp: true,
    })), draft.configuration.checkboxStyles || []),
  ];

  const mergedAccordionPanelStyleArray = [
    ...(draft.tmp?.accordionPanelStyles ? draft.tmp.accordionPanelStyles : []),
    ...withoutTwinStyles((sectionConfiguration.accordionPanelStyles || []).map((item) => ({
      ...item,
      id: `${item.id}_section_${sectionId}_time_${dateTimestamp}`,
      name: (
        <span>
          {item.name} - ( Provient de la section liée{' '}
          <FontAwesomeIcon icon={icon({ name: 'lock' })} /> : {sectionTitle})
        </span>
      ),
      fromTmp: true,
    })), [
      ...(draft.configuration.accordionPanelStyles || []),
      ...(draft.tmp?.accordionPanelStyles || []),
    ]),
  ];

  const mergedInfoCardStyleArray = [
    ...(draft.tmp?.infoCardStyles ? draft.tmp.infoCardStyles : []),
    ...withoutTwinStyles((sectionConfiguration.infoCardStyles || []).map((item) => ({
      ...item,
      id: `${item.id}_section_${sectionId}_time_${dateTimestamp}`,
      name: (
        <span>
          {item.name} - ( Provient de la section liée{' '}
          <FontAwesomeIcon icon={icon({ name: 'lock' })} /> : {sectionTitle})
        </span>
      ),
      fromTmp: true,
    })), [
      ...(draft.configuration.infoCardStyles || []),
      ...(draft.tmp?.infoCardStyles || []),
    ]),
  ];

  const mergedSelectorStyleArray = [
    ...(draft.tmp?.selectorStyles ? draft.tmp.selectorStyles : []),
    ...withoutTwinStyles(mutateImportImmutableStyleValue(
      sectionConfiguration.selectorStyles,
      draft.configuration
    ).map((item) => ({
      ...item,
      id: `${item.id}_section_${sectionId}_time_${dateTimestamp}`,
      name: (
        <span>
          {item.name} - ( Provient de la section liée{' '}
          <FontAwesomeIcon icon={icon({ name: 'lock' })} /> : {sectionTitle})
        </span>
      ),
      fromTmp: true,
    })), draft.configuration.selectorStyles || []),
  ];

  const mergedTextStylesArray = [
    ...(draft.tmp?.textStyles ? draft.tmp.textStyles : []),
    ...mutateImportImmutableStyleValue(
      sectionConfiguration.textStyles,
      draft.configuration
    ).map((item) => ({
      ...item,
      id: `${item.id}_section_${sectionId}_time_${dateTimestamp}`,
      name: (
        <span>
          {item.name} - ( Provient de la section liée{' '}
          <FontAwesomeIcon icon={icon({ name: 'lock' })} /> : {sectionTitle})
        </span>
      ),
      fromTmp: true,
    })),
  ];

  const mergedFontFamilies = [
    ...sectionConfiguration.fontFamilyList,
    ...draft.configuration.fontFamilyList,
  ].reduce((unique, item) => {
    const exists = unique.find((x) => x.key === item.key);
    if (!exists) {
      unique.push(item);
    }
    return unique;
  }, []);

  draft.configuration.fontFamilyList = mergedFontFamilies;

  draft.tmp = {
    colors: uniqueColors(mergedColorArray),
    highlightColors: uniqueColors(mergedHighlightColorArray),
    tableStyles: mergedTableStyleArray,
    checkboxStyles: mergedCheckboxStyleArray,
    accordionPanelStyles: mergedAccordionPanelStyleArray,
    infoCardStyles: mergedInfoCardStyleArray,
    selectorStyles: mergedSelectorStyleArray,
    textStyles: mergedTextStylesArray,
  };
};

export const mergeConfigurationTableStyles = (configuration, tmp) => {
  const mergedConfiguration = [
    ...configuration.tableStyles,
    ...(tmp?.tableStyles || []),
  ];
  return mergedConfiguration;
};
// Style d'un tableau, défaut du document s'il est introuvable (id suffixé
// d'une section liée jamais importé, cf. findCheckboxStyle) : le style
// générique faisait cohabiter deux rendus dans un même document.
export const findTableStyle = (configuration, tmp, styleId) => {
  const styles = mergeConfigurationTableStyles(configuration, tmp);
  return styles.find((ts) => ts.id === styleId) ?? styles[0];
};
export const mergeConfigurationCheckboxStyles = (configuration, tmp) => {
  const mergedConfiguration = [
    ...configuration.checkboxStyles,
    ...(tmp?.checkboxStyles || []),
  ];
  return mergedConfiguration;
};
// Fallback style par défaut : des proposals créés avant que
// expandLinkedSectionsAtCreation ne persiste la configuration mergée
// référencent des styles suffixés (_section_{id}_time_{ts}) absents du content.
export const findCheckboxStyle = (configuration, tmp, styleId) => {
  const styles = mergeConfigurationCheckboxStyles(configuration, tmp);
  return styles.find((ts) => ts.id === styleId) ?? styles[0];
};
export const mergeConfigurationAccordionPanelStyles = (configuration, tmp) => [
  ...(configuration.accordionPanelStyles || []),
  ...(tmp?.accordionPanelStyles || []),
];

export const mergeConfigurationInfoCardStyles = (configuration, tmp) => [
  ...(configuration.infoCardStyles || []),
  ...(tmp?.infoCardStyles || []),
];

export const mergeConfigurationSelectorStyles = (configuration, tmp) => {
  const mergedConfiguration = [
    ...configuration.selectorStyles,
    ...(tmp?.selectorStyles || []),
  ];
  return mergedConfiguration;
};
// Déduplication par ID uniquement. Surtout pas par nom : deux sections injectées
// portent le même nom de style (« Défaut - (Provient de la section : X) »), or
// chaque injection a son propre ID suffixé et ses box pointent dessus. Écarter
// un homonyme rendait son style introuvable et RichEditor retombait sur
// configuration.textStyles[0] — la police du document remplaçait celle de la
// section.
export const mergeConfigurationTextStyles = (configuration, tmp) => {
  const configStyles = configuration.textStyles || [];
  const tmpStyles = tmp?.textStyles || [];

  const seenIds = new Set();
  const merged = [];
  for (const style of [...configStyles, ...tmpStyles]) {
    if (seenIds.has(style.id)) continue;
    seenIds.add(style.id);
    merged.push(style);
  }
  return merged;
};

// Styles hérités d'une section injectée : l'ID des copies est suffixé par
// mergeStylesForProperty / mutateMergeTmpStyles, et les styles de section liée
// portent fromTmp. Sert à les masquer par défaut dans le configurateur, où ils
// se comptent par centaines sur un document à sections récursives.
export const isStyleFromSection = (style) =>
  style?.fromTmp === true ||
  (typeof style?.id === 'string' && style.id.includes('_section_'));

// Couleurs du document suivies de celles des sections liées. Une couleur de
// section n'est écartée que si le document a la même (label ET style) : un
// style identique sous un autre label est gardé, les boxes de la section s'y
// réfèrent par label.
export const mergeConfigurationColors = (configuration, tmp, dedupe = true) => {
  const merged = [...configuration.colors, ...(tmp?.colors || [])];
  return dedupe ? uniqueColors(merged) : merged;
};

export const mergeConfigurationHighlightColors = (
  configuration,
  tmp,
  dedupe = true
) => {
  const merged = [
    ...configuration.highlightColors,
    ...(tmp?.highlightColors || []),
  ];
  return dedupe ? uniqueColors(merged) : merged;
};

/**
 * Remove temporary configuration belong to a linked section.
 * @param {string} params.sectionTmpId - L'identifiant temporaire de la section à supprimer.
 * @param {Object} params.draft - Le brouillon contenant les configurations temporaires.
 */
export const removeSectionTmpConfiguration = ({ sectionTmpId, draft }) => {
  if (draft.tmp) {
    const {
      tableStyles, checkboxStyles, accordionPanelStyles, infoCardStyles,
      selectorStyles, textStyles,
    } = draft.tmp;
    draft.tmp = {
      tableStyles: tableStyles.filter(
        (item) => !sectionTmpId.includes(item.id)
      ),
      checkboxStyles: checkboxStyles.filter(
        (item) => !sectionTmpId.includes(item.id)
      ),
      accordionPanelStyles: (accordionPanelStyles || []).filter(
        (item) => !sectionTmpId.includes(item.id)
      ),
      infoCardStyles: (infoCardStyles || []).filter(
        (item) => !sectionTmpId.includes(item.id)
      ),
      selectorStyles: selectorStyles.filter(
        (item) => !sectionTmpId.includes(item.id)
      ),
      textStyles: textStyles.filter((item) => !sectionTmpId.includes(item.id)),
    };
  }
};

// Retire les doublons stricts (même label ET même style). Le label identifie la
// couleur (les boxes s'y réfèrent) : deux couleurs de même style mais de labels
// différents sont gardées, sinon les boxes pointant sur le label retiré
// perdraient leur couleur.
const colorKey = (color) => `${color.label}|${color.style}`;

export const uniqueColors = (colors) => {
  const seen = new Set();
  return (colors || []).filter((item) => {
    const key = colorKey(item);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Couleurs de `colors` absentes de `reference`, avec la même règle que
// uniqueColors (label ET style).
export const colorsNotIn = (colors, reference) => {
  const referenceKeys = new Set((reference || []).map(colorKey));
  return uniqueColors(colors).filter(
    (item) => !referenceKeys.has(colorKey(item))
  );
};

// Nettoie au chargement les documents déjà enregistrés avec des palettes
// multipliées par l'ancien merge de sections.
export const withUniqueColors = (configuration) => ({
  ...configuration,
  colors: uniqueColors(configuration.colors),
  highlightColors: uniqueColors(configuration.highlightColors),
});

export const filterSameColor = (colors) => {
  const uniqueStyles = new Set();

  return colors.filter((item) => {
    if (uniqueStyles.has(item.style)) return false;

    uniqueStyles.add(item.style);
    return true;
  });
};

/**
 * Ratio d'interligne sans unité (modèle Word / Google Docs).
 *
 * Word et Google Docs utilisent un multiplicateur fixe appliqué à la taille de
 * police du paragraphe (ex. « Simple » = 1.15, « 1,5 » = 1.5). Le résultat en
 * pixels est calculé par le navigateur : hauteur de ligne = fontSize × ratio.
 *
 * Exemples avec ratio 1.4 :
 *   - fontSize 14 → 19.6 px
 *   - fontSize 50 → 70 px
 *
 * Le ratio ne doit pas être recalculé en fonction de fontSize : un ratio
 * unitless appliqué sur le même élément que fontSize scale déjà correctement.
 *
 * @param {number} fontSize - Taille de police en px (garde la signature pour l'API)
 * @param {number} baseLineHeight - Multiplicateur d'interligne (défaut: 1.4, proche du 1.15 Word/Docs)
 * @param {number} minLineHeight - Plancher du multiplicateur (défaut: 1.2)
 * @returns {number} Multiplicateur unitless pour la propriété CSS line-height
 */
export const calculateOptimalLineHeight = (
  fontSize,
  _baseFontSize = 14,
  baseLineHeight = 1.4,
  minLineHeight = 1.2
) => {
  if (!fontSize || fontSize <= 0) {
    return baseLineHeight;
  }

  return Math.max(minLineHeight, baseLineHeight);
};

export { LIGHT_COLOR_LUMINANCE, isLightColor } from '../shared/colors/lightColor';
